import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { Loader2, Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function AcceptInvitePage() {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const validatedRef = useRef(false); // Prevent StrictMode double-call

  useEffect(() => {
    if (!token) { setError('No invite token provided'); setLoading(false); return; }
    if (validatedRef.current) return; // Already validated
    validatedRef.current = true;
    authAPI.validateInviteToken(token)
      .then(res => {
        if (res.data.valid) {
          setValid(true);
          setUserName(res.data.name || '');
          setUserEmail(res.data.email || '');
        } else {
          // Token might have been consumed by a previous attempt — check if user can already log in
          setError(res.data.error || 'Invalid invite token');
        }
      })
      .catch(() => setError('Failed to validate invite token'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setSubmitting(true);
    try {
      const res = await authAPI.acceptInvite(token, password);
      // Auto-login and redirect to /tasks
      localStorage.setItem('stellar_token', res.data.token);
      localStorage.setItem('stellar_user', JSON.stringify(res.data.user));
      login(res.data.token, res.data.user);
      toast.success(`Welcome to Stellar, ${res.data.user.name}!`);
      // Force redirect in case login doesn't trigger navigation
      window.location.href = '/tasks';
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      // If token already used, user might already have an account — guide them to login
      if (detail.includes('already-used') || detail.includes('expired')) {
        setError('');
        toast.info('Your account may already be set up. Try logging in.');
        window.location.href = '/login?email=' + encodeURIComponent(userEmail);
      } else {
        toast.error(detail || 'Failed to set password');
      }
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-gold" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md bg-card/80 backdrop-blur border-border/50">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-display font-semibold mb-2">Invite Issue</h2>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <p className="text-xs text-muted-foreground/50 mb-6">If you've already set your password, you can log in directly.</p>
          <div className="flex gap-3 justify-center">
            <a href="/login" className="text-gold hover:underline text-sm font-medium">Go to Login</a>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="accept-invite-page">
      <Card className="w-full max-w-md bg-card/80 backdrop-blur border-border/50">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="h-16 w-16 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-gold" />
            </div>
            <h2 className="text-xl font-display font-semibold mb-2">Welcome, {userName}!</h2>
            <p className="text-sm text-muted-foreground">Create your password to access Stellar Productions.</p>
            <p className="text-xs text-muted-foreground/50 mt-1">{userEmail}</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[13px]">Create Password</Label>
              <PasswordInput placeholder="At least 6 characters" value={password} onChange={e => setPassword(e.target.value)} className="h-11 text-[14px]" data-testid="invite-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[13px]">Confirm Password</Label>
              <PasswordInput placeholder="Type your password again" value={confirm} onChange={e => setConfirm(e.target.value)} className="h-11 text-[14px]" data-testid="invite-confirm-password" />
            </div>
            <Button type="submit" className="w-full h-11 text-[14px] bg-gold/90 hover:bg-gold text-black font-semibold" disabled={submitting} data-testid="invite-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set Password & Join'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
